import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Tooltip } from './tooltip';

export type TagTone = 'neutral' | 'pos' | 'neg' | 'warn' | 'info';

const TONES: Record<TagTone, string> = {
  neutral: 'bg-wash text-muted',
  pos: 'bg-pos-wash text-pos-ink',
  neg: 'bg-neg-wash text-neg-ink',
  warn: 'bg-warn-wash text-warn-ink',
  info: 'bg-accent-wash text-accent-ink',
};

type TagProps = ComponentProps<'span'> & { tone?: TagTone };

/** A quiet pill for a state or a category. Never colour alone: the text says it. */
export function Tag({ tone = 'neutral', className, ...props }: TagProps) {
  return (
    <span
      className={cn('inline-flex h-[22px] items-center gap-1 rounded-pill px-2 text-2xs font-medium whitespace-nowrap', TONES[tone], className)}
      {...props}
    />
  );
}

/**
 * The backend's status model (apps/api/api/services/FOCUS.md): six verdicts
 * plus two honest non-verdicts — `inactive` (no spend in the last days of the
 * window) and `not_enough_data` (below the minimums, or no previous window).
 * `scaling`: its share of the network's spend is rising and efficiency held.
 * `ready_to_scale`: little spend, but beating an account target.
 * `expanding`: efficiency dipped because delivery grew sharply, not fatigue.
 * The one list every status filter, lane and guard derives from.
 */
export const ASSET_STATUSES = ['scaling', 'ready_to_scale', 'steady', 'expanding', 'fatiguing', 'new', 'inactive', 'not_enough_data'] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];
export const isAssetStatus = (value: string): value is AssetStatus => (ASSET_STATUSES as readonly string[]).includes(value);

/** Glyph + word: the status reads without colour (DESIGN.md §Accessibility). */
export const STATUS_LABEL: Record<AssetStatus, string> = {
  scaling: '▲ Scaling',
  ready_to_scale: '△ Ready to scale',
  steady: '● Steady',
  expanding: '↗ Expanding',
  fatiguing: '▼ Fatiguing',
  new: '◆ New',
  inactive: '‖ Inactive',
  not_enough_data: '◌ Not enough data',
};
const STATUS_TONE: Record<AssetStatus, TagTone> = {
  scaling: 'pos',
  ready_to_scale: 'pos',
  steady: 'neutral',
  expanding: 'info',
  fatiguing: 'neg',
  new: 'info',
  inactive: 'neutral',
  not_enough_data: 'neutral',
};

type StatusPillProps = {
  status: AssetStatus;
  /**
   * Evidence lives where a conclusion is drawn: pass the rule, the reason and
   * the data-sufficiency line (with minimums). The pill becomes focusable so
   * keyboard users reach the tooltip.
   */
  evidence?: ReactNode;
  className?: string;
};

export function StatusPill({ status, evidence, className }: StatusPillProps) {
  const pill = (
    <Tag tone={STATUS_TONE[status]} className={cn(evidence ? 'cursor-help' : undefined, className)} {...(evidence ? { tabIndex: 0 } : {})}>
      {STATUS_LABEL[status]}
    </Tag>
  );
  return evidence ? <Tooltip content={evidence}>{pill}</Tooltip> : pill;
}

export type EvidenceLevel = 'enough' | 'partial' | 'not-enough';

const EVIDENCE_TONE: Record<EvidenceLevel, TagTone> = { enough: 'pos', partial: 'warn', 'not-enough': 'neg' };

/**
 * Data-sufficiency label, e.g. in the app switcher: "Enough data",
 * "Enough data at 28 days", "Not enough data yet". The label must say what
 * the evidence is for — a bare "Strong evidence" badge was rejected.
 */
export function EvidencePill({ level, children }: { level: EvidenceLevel; children: ReactNode }) {
  return (
    <Tag tone={EVIDENCE_TONE[level]} className="gap-[5px]">
      <span aria-hidden="true" className={cn('size-1.5 bg-current', level === 'not-enough' ? 'rounded-[1px]' : 'rounded-full')} />
      {children}
    </Tag>
  );
}
